import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDemo } from '@/contexts/DemoContext';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';

export function DemoOverlay() {
  const isMobile = useIsMobile();
  const { isDemoActive, isPreparing, currentScenario, currentStep, currentStepIndex, totalSteps, isExecutingAction, nextStep, prevStep, exitDemo, showIntro, transitionError, transitionFailedOp, retryTransition, retryCheckpointRead, forceExitDemo, checkpoint, failedChapterIndex, committedChapterIndex } = useDemo();
  const blocked = !!transitionError;
  const [highlightRect, setHighlightRect] = useState<DOMRect | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  // Track the step index we last measured for — prevents stale measurements
  const measuredStepRef = useRef<number>(-1);
  // Track whether card has been shown at least once (for initial fade-in only)
  const [hasAppeared, setHasAppeared] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{ dx: number; dy: number } | null>(null);

  // Keyboard shortcuts live in DemoToolbar (guarded by isTypingOrModalTarget).

  const getBestHighlightElement = useCallback((): HTMLElement | null => {
    if (!currentStep?.highlight) return null;

    const candidates = Array.from(
      document.querySelectorAll(`[data-demo="${currentStep.highlight}"]`)
    ) as HTMLElement[];

    if (candidates.length === 0) return null;

    const visibleCandidates = candidates.filter((el) => {
      const style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) {
        return false;
      }
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });

    // On desktop, ignore hidden mobile drawer duplicates when possible
    const desktopScoped = isMobile
      ? visibleCandidates
      : visibleCandidates.filter((el) => !el.closest('[data-vaul-drawer]'));

    const pool = desktopScoped.length > 0 ? desktopScoped : visibleCandidates;
    if (pool.length === 0) return null;

    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    let bestEl: HTMLElement | null = null;
    let bestVisibleArea = -1;

    for (const el of pool) {
      const rect = el.getBoundingClientRect();
      const visibleWidth = Math.max(0, Math.min(rect.right, viewportW) - Math.max(rect.left, 0));
      const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportH) - Math.max(rect.top, 0));
      const visibleArea = visibleWidth * visibleHeight;
      if (visibleArea > bestVisibleArea) {
        bestVisibleArea = visibleArea;
        bestEl = el;
      }
    }

    return bestEl;
  }, [currentStep?.highlight, isMobile]);

  // Stable measure function — picks best visible target and keeps highlight locked to it
  const measure = useCallback((options?: { scrollIntoView?: boolean }) => {
    const el = getBestHighlightElement();
    if (!el) return false;

    const scrollIntoViewEnabled = options?.scrollIntoView ?? true;
    const rect = el.getBoundingClientRect();
    const outsideSafeViewport = rect.top < 72 || rect.bottom > window.innerHeight - 72;
    const isBroadHighlight = currentStep?.highlight === 'dependency-connectors' || currentStep?.highlight === 'gantt-chart';
    const isLargeTarget = rect.height > window.innerHeight * 0.85 || rect.width > window.innerWidth * 0.85;

    // Avoid auto-scrolling for broad/large targets (e.g. full Gantt/connectors),
    // otherwise the viewport can jump during demo steps and appear visually misaligned.
    if (scrollIntoViewEnabled && outsideSafeViewport && !isBroadHighlight && !isLargeTarget) {
      el.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' });
    }

    requestAnimationFrame(() => {
      const liveTarget = getBestHighlightElement();
      if (!liveTarget) return;
      const liveRect = liveTarget.getBoundingClientRect();
      setHighlightRect(liveRect);
    });

    return true;
  }, [getBestHighlightElement]);

  // Main measurement effect — waits for auto-actions to finish before measuring
  useEffect(() => {
    if (!isDemoActive || !currentStep) return;

    // If step has no highlight, clear rect immediately
    if (!currentStep.highlight) {
      setHighlightRect(null);
      measuredStepRef.current = currentStepIndex;
      return;
    }

    // While auto-actions are executing, don't measure yet — the target may not be in DOM
    if (isExecutingAction) {
      return;
    }

    // Actions done (or step has none) — now measure
    measuredStepRef.current = currentStepIndex;

    if (!measure({ scrollIntoView: true })) {
      // Retry with increasing delays if target not found yet
      const retryDelays = [100, 250, 500, 800];
      const timers = retryDelays.map((ms, idx) =>
        setTimeout(() => {
          if (!measure({ scrollIntoView: true }) && idx === retryDelays.length - 1) {
            // Give up — show card centered (null rect)
            setHighlightRect(null);
          }
        }, ms)
      );
      return () => timers.forEach(clearTimeout);
    }
  }, [isDemoActive, currentStep, currentStepIndex, isExecutingAction, measure]);

  // Keep highlight position updated and sticky to moving targets
  useEffect(() => {
    if (!isDemoActive || !currentStep?.highlight || isExecutingAction) return;

    const onReposition = () => measure({ scrollIntoView: false });
    const stickyInterval = window.setInterval(onReposition, 120);

    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);

    return () => {
      window.clearInterval(stickyInterval);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [isDemoActive, currentStep?.highlight, isExecutingAction, measure]);

  // Initial appearance
  useEffect(() => {
    if (isDemoActive && !hasAppeared) setHasAppeared(true);
    if (!isDemoActive) setHasAppeared(false);
  }, [isDemoActive, hasAppeared]);

  const persona = useMemo(() => {
    if (!currentScenario || !currentStep) return null;
    return currentScenario.personas[currentStep.actAsSlot] ?? null;
  }, [currentScenario, currentStep]);

  const isBusy = isPreparing || isExecutingAction;

  if (!isDemoActive || !currentStep || isMobile || showIntro) return null;

  const cardStyle: React.CSSProperties = pos
    ? { position: 'fixed', left: pos.x, top: pos.y, width: collapsed ? 280 : 380, zIndex: 10020 }
    : { position: 'fixed', right: 16, bottom: 16, width: collapsed ? 280 : 380, zIndex: 10020 };

  const onDragStart = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    const card = cardRef.current;
    if (!card) return;
    const r = card.getBoundingClientRect();
    dragRef.current = { dx: e.clientX - r.left, dy: e.clientY - r.top };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onDragMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    const card = cardRef.current;
    if (!d || !card) return;
    const w = card.offsetWidth, h = card.offsetHeight;
    setPos({
      x: Math.max(8, Math.min(e.clientX - d.dx, window.innerWidth - w - 8)),
      y: Math.max(8, Math.min(e.clientY - d.dy, window.innerHeight - h - 8)),
    });
  };
  const onDragEnd = () => { dragRef.current = null; };

  const overlay = (
    <>
      {highlightRect && (
        <div
          aria-hidden
          className="fixed z-[9997] pointer-events-none rounded-lg border-2 border-primary"
          style={{
            top: highlightRect.top - 4, left: highlightRect.left - 4,
            width: highlightRect.width + 8, height: highlightRect.height + 8,
            transition: 'top 0.3s ease, left 0.3s ease, width 0.3s ease, height 0.3s ease',
          }}
        />
      )}

      <section
        ref={cardRef}
        role="region"
        aria-label="Walkthrough"
        data-demo-narrative-card
        className="pointer-events-auto rounded-xl bg-popover text-popover-foreground border border-border shadow-2xl"
        style={cardStyle}
      >
        <div
          className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border cursor-move select-none touch-none"
          onPointerDown={onDragStart} onPointerMove={onDragMove} onPointerUp={onDragEnd} onPointerCancel={onDragEnd}
          onDoubleClick={() => setPos(null)}
          title="Drag to move · double-click to dock"
        >
          <span className="text-[10px] font-mono text-muted-foreground truncate">
            Chapter {currentStepIndex + 1} of {totalSteps}{currentStep.title ? ` · ${currentStep.title}` : ''}
          </span>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-6 w-6" aria-label={collapsed ? 'Expand walkthrough' : 'Collapse walkthrough'} aria-expanded={!collapsed} onClick={() => setCollapsed(c => !c)}>
              {collapsed ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>

        {!collapsed && (
          <div className="p-3 space-y-3">
            {persona && (
              <div className="flex items-center gap-1.5">
                {persona.avatarUrl && <img src={persona.avatarUrl} alt="" className="w-5 h-5 rounded-full object-cover" />}
                <span className="text-xs font-medium">{persona.name}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent text-accent-foreground capitalize">{persona.role}</span>
              </div>
            )}
            <p className="text-sm leading-relaxed">
              {currentStep.narrative.split(/\*\*(.*?)\*\*/g).map((part, i) => (i % 2 === 0 ? part : <strong key={i}>{part}</strong>))}
            </p>
            {currentStep.featureCallout && (
              <p className="text-xs text-muted-foreground italic border-l-2 border-primary pl-2">{currentStep.featureCallout}</p>
            )}
            {currentStep.explore && <p className="text-xs text-muted-foreground">Try it: {currentStep.explore}</p>}
            {currentStep.fixture && <p className="text-[11px] text-muted-foreground">Scripted sample setup — not a live decision.</p>}

            {checkpoint && (
              <div role="status" className="rounded-md border border-primary/40 bg-primary/5 p-2 space-y-1">
                <p className="text-xs font-medium">
                  Waiting for your decision on “{checkpoint.target}” ({checkpoint.expected === 'approved' ? 'approve' : 'request changes'}).
                </p>
                <p className="text-[11px] text-muted-foreground">
                  Saved status: {checkpoint.saved ?? 'unknown'}.{checkpoint.saved && checkpoint.saved !== 'pending' && checkpoint.saved !== checkpoint.expected ? ' That differs from this chapter — Next will rebuild with a scripted decision.' : ''}
                  {' '}Next skips the live decision and records a scripted one.
                </p>
                {checkpoint.readError && (
                  <div role="alert" className="space-y-1 pt-1">
                    <p className="text-xs text-destructive">{checkpoint.readError}</p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={retryCheckpointRead}>Retry</Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={exitDemo}>Exit walkthrough</Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {transitionError && (
              <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 p-2 space-y-2">
                {failedChapterIndex != null && (
                  <p className="text-xs font-medium text-foreground">
                    Chapter {failedChapterIndex + 1} did not finish loading. The screen may already show part of it; the last fully loaded chapter is {committedChapterIndex + 1}.
                  </p>
                )}
                <p className="text-xs text-destructive">{transitionError}</p>
                <p className="text-[11px] text-muted-foreground">Retry this chapter, pick a chapter from the menu, or exit.</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={retryTransition}>Retry</Button>
                  {transitionFailedOp === 'exit'
                    ? <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={forceExitDemo}>Leave without restoring</Button>
                    : <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={exitDemo}>Exit walkthrough</Button>}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between px-3 pb-3 pt-1">
          <Button variant="ghost" size="sm" onClick={prevStep} disabled={currentStepIndex <= 0 || isBusy || blocked} className="h-7 text-xs" aria-label="Previous chapter">
            <ChevronLeft className="h-3.5 w-3.5 mr-1" />Back
          </Button>
          {isBusy && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground" aria-live="polite">
              <Loader2 className="h-3 w-3 animate-spin" />
              {isPreparing ? 'Preparing…' : 'Loading chapter…'}
            </div>
          )}
          {currentStep.isFinalStep ? (
            <Button variant="destructive" size="sm" onClick={exitDemo} disabled={isBusy} className="h-7 text-xs">
              <X className="h-3.5 w-3.5 mr-1" />Close walkthrough
            </Button>
          ) : (
            <Button variant="default" size="sm" onClick={nextStep} disabled={currentStepIndex >= totalSteps - 1 || isBusy || blocked} className="h-7 text-xs" aria-label="Next chapter">
              Next<ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          )}
        </div>
      </section>
    </>
  );

  return createPortal(overlay, document.body);
}
